import format from 'date-fns/format';
import { ComponentProps, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';

import { buttonStyles } from './Button';
import { Icon } from './Icon';
import { cn } from './utils/cn';

type CalendarProps = ComponentProps<typeof DayPicker> & {
  footer?: React.ReactNode;
};

export const Calendar = ({
  className,
  classNames: __classNames,
  showOutsideDays = true,
  footer,
  ...props
}: CalendarProps) => {
  const hasSelectedRange = Boolean(
    props.mode === 'range' && props.selected?.from && props.selected?.to,
  );

  const dayClassName = useMemo(() => {
    return cn(
      // Center text, set cell height and padding, vertical align
      'text-center h-8 py-0 px-1 my-auto relative',
      // Focused cell brought above others
      'focus-within:relative focus-within:z-20',
      // only apply range background styles if there is a selected range
      hasSelectedRange && [
        // TODO(chris)[ELU-5054]: This could be difficult to maintain in the future. See if there's a better way to handle multiple classes being applied to the same day.
        // apply right-half background to start of range cell
        '[&:has(.range-start):not(:has(.invisible))]:bg-[linear-gradient(to_right,transparent_50%,hsl(var(--color-bg-quaternary))_50%)]',
        // apply left-half background to end of range cell
        '[&:has(.range-end):not(:has(.invisible))]:bg-[linear-gradient(to_left,transparent_50%,hsl(var(--color-bg-quaternary))_50%)]',
        // apply background to middle of the range cells
        '[&:has([aria-selected]):not(:has(.range-start)):not(:has(.range-end)):not(:has(.invisible))]:bg-bg-quaternary',
      ],
    );
  }, [hasSelectedRange]);

  const todayClassName = useMemo(() => {
    const isRange = props.mode === 'range';

    return cn(
      isRange
        ? `[&>button:not(.range-start):not(.range-end)]:bg-text-supporting!`
        : `[&>button:not(.day-selected)]:bg-text-supporting!`,
      isRange
        ? `[&>button:not(.range-start):not(.range-end)]:hover:bg-text-supporting!`
        : `[&>button:not(.day-selected)]:hover:bg-text-supporting!`,
      '[&>button]:text-bg-tertiary!',
      // custom class name that allows us to target the today's cell
      'day-today',
    );
  }, [props.mode]);

  return (
    <DayPicker
      // always set this to true, otherwise, the empty cells don't render properly
      // we hide outside days with the outside class
      showOutsideDays={true}
      className={cn('relative p-4', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row',
        month: '',
        month_caption: 'flex justify-center h-8 relative items-center',
        caption_label: 'text-sm font-bold text-text-tertiary',
        nav: 'absolute top-8 inset-x-4 flex items-center justify-between space-x-1 z-10',
        button_previous: buttonStyles({ variant: 'ghost', size: 'sm' }).button({
          className: 'absolute! left-1 aspect-square px-0',
        }),
        button_next: buttonStyles({ variant: 'ghost', size: 'sm' }).button({
          className: 'absolute! right-1 aspect-square px-0',
        }),
        month_grid: 'w-full border-collapse space-y-1 mt-4',
        weekdays: 'flex mb-2',
        weekday: 'w-10 p-0 text-xs font-bold text-text-supporting uppercase',
        week: 'flex w-full h-10',
        day: dayClassName,
        day_button:
          'size-8 rounded-lg text-sm font-semibold text-text-secondary hover:bg-bg-quaternary',
        selected:
          '[&>button]:!bg-text-primary [&>button]:!text-bg-tertiary day-selected',
        today: todayClassName,
        outside: `[&>button]:!text-text-disabled${!showOutsideDays ? ' invisible' : ''}`,
        disabled: '[&>button]:!text-text-disabled',
        range_start:
          '[&>button]:!bg-text-primary [&>button]:!text-bg-tertiary range-start',
        range_end:
          '[&>button]:!bg-text-primary [&>button]:!text-bg-tertiary range-end',
        range_middle: cn(
          // don't override today styles if the day is in the middle of a range
          'aria-selected:[&:not(.day-today)]:bg-bg-quaternary!',
          'aria-selected:hover:[&:not(.day-today)]:bg-border-secondary!',
          'aria-selected:[&:not(.day-today)]:text-text-primary!',
        ),
        hidden: 'invisible',
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <Icon name="ChevronLeft" />
          ) : (
            <Icon name="ChevronRight" />
          ),
      }}
      formatters={{
        formatWeekdayName: (date) => format(date, 'ccc'),
      }}
      footer={
        footer ? (
          <>
            <div className="my-3 h-px w-full bg-border-primary" />
            {footer}
          </>
        ) : undefined
      }
      {...props}
    />
  );
};
